<template lang="pug">
    PrintablePanel
      FormErrorAlert(
        message="You have exceeded the limit of 300 agencies returned. If there are agencies missing that need to be on the report, please refine the search and try again."
        v-if="results.length > 299"
        :dismissable="false"
      )
      PageHeader Agency Usage Report
      .row.mt-2
        .col-8
          div
            | Results for
            strong  {{ startDate }} - {{ endDate }}:
          h5
            | {{ totalActiveUsers }} Active Users, {{ totalLogins }} Logins, {{ totalSearches }} Searches,
            | {{ totalReports }} Report Queries, {{ totalActiveWatches }} Active Watches,
            | {{ totalWatchHits }} Active Watch Hits
        .col.text-right(v-if="!printable")
          button.btn.btn-primary.mr-2(@click.prevent="downloadCsv('agencyusage.csv', csvRecords)") Download CSV
          router-link.btn.btn-primary(:to="{query: {print: true}}" target="_blank") Print
      Datatable.mt-4(
        :columns="columns"
        :entries="results"
        header-class="h7"
        :per-page="20"
        sortable
        :sort-keys="sortKeys"
        @sortTable="sortResults"
      )
        template(slot-scope="props")
          tr.body-3
            td {{ props.entry.stateCd}}
            td {{ props.entry.agencyDescription }}
            td {{ props.entry.activeUserTotal }}
            td {{ props.entry.loginTotal }}
            td {{ props.entry.searchTotal }}
            td {{ props.entry.reportTotal }}
            td {{ props.entry.activeWatchTotal }}/{{ props.entry.watchHitTotal }}
</template>

<script>
import PageHeader from '@/components/PageHeader'
import PrintablePanel from '@/components/PrintablePanel'
import { formItem, clearForm } from '@/lib/formstore'
import axios from '@/lib/axios'
import sum from 'lodash/sum'
import Datatable from '@/components/Datatable'
import { globalError } from '@/lib/utils'
import { subDays, format } from 'date-fns/esm'
import { downloadCsv } from '@/lib/csv'

function getAgencyUsageItems() {
  return {
    agencyType: formItem('agencyusage', 'agencyType') || 'all',
    startDate:
      formItem('agencyusage', 'startDate') ||
      format(subDays(new Date(), 30), 'MM/dd/yyyy'),
    endDate:
      formItem('agencyusage', 'endDate') ||
      format(subDays(new Date(), 30), 'MM/dd/yyyy'),
    selectedAgencyIds: formItem('agencyusage', 'selectedAgencyIds') || [],
  }
}

export default {
  name: 'AgencyUsageReport',
  data() {
    return {
      ...getAgencyUsageItems(),
      results: [],
      columns: [
        'State',
        'Agency',
        '# of Active Users',
        '# of Logins',
        '# of Searches',
        '# of Report Queries',
        '# of Active Watches/Hits',
      ],
      sortKeys: [
        'stateCd',
        'agencyDescription',
        'activeUserTotal',
        'loginTotal',
        'searchTotal',
        'reportTotal',
        'activeWatchTotal',
      ],
    }
  },
  methods: {
    sortResults({ sortKey, sortDir }) {
      this.results.sort((a, b) => {
        let A = a[sortKey]
        let B = b[sortKey]
        if (sortKey === 'activeWatchTotal' && A === B) {
          A = a['watchHitTotal']
          B = b['watchHitTotal']
        }
        return A < B ? -1 : A > B ? 1 : 0
      })
      if (sortDir === 'desc') this.results.reverse()
    },
    downloadCsv,
  },
  computed: {
    totalActiveUsers() {
      return sum(this.results.map(r => r.activeUserTotal))
    },
    totalLogins() {
      return sum(this.results.map(r => r.loginTotal))
    },
    totalSearches() {
      return sum(this.results.map(r => r.searchTotal))
    },
    totalReports() {
      return sum(this.results.map(r => r.reportTotal))
    },
    totalActiveWatches() {
      return sum(this.results.map(r => r.activeWatchTotal))
    },
    totalWatchHits() {
      return sum(this.results.map(r => r.watchHitTotal))
    },
    csvRecords() {
      return this.results.map(r => {
        return {
          State: r.stateCd,
          Agency: r.agencyDescription,
          '# of Active Users': r.activeUserTotal,
          '# of Logins': r.loginTotal,
          '# of Searches': r.searchTotal,
          '# of Report Queries': r.reportTotal,
          '# of Active Watches/Hits': `${r.activeWatchTotal}/${r.watchHitTotal}`,
        }
      })
    },
  },
  beforeRouteEnter(_to, _from, next) {
    let {
      agencyType,
      startDate,
      endDate,
      selectedAgencyIds,
    } = getAgencyUsageItems()
    axios()
      .post('/api/admin/agency_usage_report', {
        agencies:
          formItem('agencyusage', 'agencyType') === 'all'
            ? []
            : formItem('agencyusage', 'selectedAgencies').map(a => a.id),
        startDate,
        endDate,
        statesList: formItem('agencyusage', 'selectedStates').map(
          s => s.abbreviation,
        ),
      })
      .then(resp => {
        scrollTo(0, 0)
        next(vm => {
          vm.results = resp.data
          if (vm.printable) vm.$nextTick(() => window.print())
        })
      })
      .catch(err => globalError(err))
  },
  components: { PageHeader, Datatable, PrintablePanel },
}
</script>

<style lang="scss" scoped>
.panel-white {
  width: 1007px;
}
</style>
